import { useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ShoppingCart, ArrowRight, Trash2, MessageSquare } from "lucide-react";
import { Reveal, BigHead } from "./Reveal";
import { openContact } from "./ContactDialog";
import { ContentCtx, cv } from "@/lib/content";

export const ITEMS = [
  { id: "business-cards", name: "Business Cards", desc: "Thick stock, bold ink, unforgettable handoffs.", color: "yellow", price: 75,
    options: [{ label: "Qty 100", price: 75 }, { label: "Double Sided — Qty 100", price: 80 }] },
  { id: "banners", name: "Banners", desc: "Big, loud, weatherproof statements.", color: "cyan", price: 100,
    options: [{ label: "Standard", price: 100 }, { label: "Medium 6x3", price: 130 }, { label: "Large 8x4", price: 170 }, { label: "XL 9x4", price: 210 }] },
  { id: "acuity-site", name: "Acuity Booking Site", desc: "A booking site that fills your calendar while you sleep.", color: "ink", price: 150 },
  { id: "flyers", name: "Flyers", desc: "Street-ready promo that actually gets read.", color: "yellow", price: 50,
    options: [{ label: "Digital Only", price: 50 }, { label: "Printed — Qty 25", price: 75 }, { label: "Printed — Qty 50", price: 100 }, { label: "Printed — Qty 100", price: 125 }] },
  { id: "logo-design", name: "Logo Design", desc: "The mark everything else is built on.", color: "magenta", price: 150 },
  { id: "obituaries", name: "Obituaries", desc: "Honor a life beautifully — memorial programs, tributes, and keepsake prints designed with care.", color: "magenta", price: null, tag: "Available on request" },
];

const CartCard = ({ item, i, cart, setQty }) => {
  const [optIdx, setOptIdx] = useState(0);
  const hasOpts = !!item.options?.length;
  const key = hasOpts ? `${item.id}::${optIdx}` : item.id;
  const qty = cart[key] || 0;
  return (
    <motion.div
      data-testid={`cart-item-${item.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
      className={`border-4 border-ink bg-white p-5 transition-all ${qty ? "shadow-hard" : "shadow-hard-sm hover:-translate-y-1"}`}
    >
      <span className="ink-dot block h-9 w-9" style={{ background: cv(item.color), border: item.color === "yellow" ? "2px solid var(--ink)" : "none" }} />
      <h3 className="mt-4 font-display uppercase text-xl leading-tight text-ink">{item.name}</h3>
      <p className="mt-2 text-sm font-medium text-ink/60 leading-relaxed">{item.desc}</p>
      {item.tag ? (
        <span data-testid={`cart-price-${item.id}`} className="mt-3 inline-block border-2 border-ink bg-staxmagenta px-2.5 py-1 font-mono2 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
          {item.tag}
        </span>
      ) : item.price ? (
        <span data-testid={`cart-price-${item.id}`} className="mt-3 inline-block border-2 border-ink bg-staxyellow px-2.5 py-1 font-mono2 text-[11px] font-bold uppercase tracking-[0.15em] text-ink">
          From ${item.price}
        </span>
      ) : (
        <span data-testid={`cart-price-${item.id}`} className="mt-3 inline-block border-2 border-dashed border-ink/40 px-2.5 py-1 font-mono2 text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">
          Custom quote
        </span>
      )}
      {hasOpts && (
        <select
          data-testid={`cart-option-${item.id}`}
          value={optIdx}
          onChange={(e) => setOptIdx(Number(e.target.value))}
          className="mt-3 w-full border-2 border-ink bg-white px-3 py-2.5 font-mono2 text-[11px] font-bold uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-staxmagenta"
        >
          {item.options.map((o, oi) => (
            <option key={o.label} value={oi}>{o.label} — ${o.price}</option>
          ))}
        </select>
      )}
      <div className="mt-5">
        {qty === 0 ? (
          <button
            data-testid={`cart-add-${item.id}`}
            onClick={() => setQty(key, 1)}
            className="inline-flex w-full items-center justify-center gap-2 border-2 border-ink bg-staxcyan px-4 py-2.5 font-display uppercase text-sm text-ink transition-colors hover:bg-staxmagenta hover:text-white"
          >
            <Plus size={16} /> Add to Cart
          </button>
        ) : (
          <div className="flex items-center justify-between border-2 border-ink">
            <button
              data-testid={`cart-dec-${item.id}`}
              onClick={() => setQty(key, qty - 1)}
              aria-label="Decrease quantity"
              className="px-4 py-2.5 hover:bg-staxmagenta hover:text-white transition-colors"
            >
              <Minus size={16} />
            </button>
            <span data-testid={`cart-qty-${item.id}`} className="font-display text-lg">{qty}</span>
            <button
              data-testid={`cart-inc-${item.id}`}
              onClick={() => setQty(key, qty + 1)}
              aria-label="Increase quantity"
              className="px-4 py-2.5 hover:bg-staxcyan transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const BuildCart = ({ ch = "02" }) => {
  const content = useContext(ContentCtx);
  const items = content?.cart_items?.length ? content.cart_items : ITEMS;
  const [cart, setCart] = useState({});
  const [customDraft, setCustomDraft] = useState("");
  const [customRequest, setCustomRequest] = useState("");

  const setQty = (id, qty) =>
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });

  const addCustom = () => {
    const v = customDraft.trim();
    if (!v) return;
    setCustomRequest(v);
    setCustomDraft("");
  };

  const lines = Object.entries(cart)
    .map(([key, qty]) => {
      const [itemId, optIdx] = key.split("::");
      const item = items.find((i) => i.id === itemId);
      if (!item) return null;
      const opt = item.options?.[Number(optIdx)] || null;
      const price = opt ? opt.price : item.price;
      return { key, qty, item, opt, price };
    })
    .filter(Boolean);
  const totalItems = lines.reduce((sum, l) => sum + l.qty, 0);
  const estimate = lines.reduce((sum, l) => sum + (l.price ? l.price * l.qty : 0), 0);
  const customCount = lines.filter((l) => !l.price).length;
  const cartEmpty = lines.length === 0 && !customRequest;

  const checkout = () => {
    const summary = lines
      .map((l) => `${l.qty}x ${l.item.name}${l.opt ? ` — ${l.opt.label}` : ""}${l.price ? ` ($${l.price})` : ""}`)
      .join(", ");
    openContact(
      `Hi! I'd like to start a project${summary ? ` with: ${summary}` : ""}.${estimate ? ` Estimated from $${estimate}.` : ""}${customRequest ? ` Custom request: "${customRequest}"` : ""}`
    );
  };

  return (
    <section id="cart" data-testid="cart-section" className="relative overflow-hidden bg-paper py-24 sm:py-32 border-t-4 border-ink">
      <div className="ink-dot absolute -right-24 -top-16 h-72 w-72 bg-staxyellow" />
      <div className="halftone absolute left-0 bottom-0 h-64 w-64 opacity-10 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal delay={0.08}>
            <BigHead>Build Your<br />Cart<span className="text-staxmagenta">.</span></BigHead>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="max-w-sm text-ink/60 font-medium text-base">
              Pick your pieces — print, digital, whatever the brand needs. We'll make every one of them beautiful.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {items.map((item, i) => (
              <CartCard key={item.id} item={item} i={i} cart={cart} setQty={setQty} />
            ))}

            <motion.div
              data-testid="cart-custom-box"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className="sm:col-span-2 border-4 border-dashed border-ink bg-staxyellow p-6"
            >
              <div className="flex items-center gap-2.5">
                <MessageSquare size={22} className="text-ink" />
                <h3 className="font-display uppercase text-2xl text-ink">Don't see what you need?</h3>
              </div>
              <p className="mt-2 text-sm font-medium text-ink/65">
                Type here and tell me what you need — we'll talk.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="cart-custom-input"
                  value={customDraft}
                  onChange={(e) => setCustomDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCustom()}
                  placeholder="e.g. Yard signs for my grand opening..."
                  maxLength={120}
                  className="flex-1 border-2 border-ink bg-white px-4 py-3 font-body text-sm font-medium focus:outline-none focus:ring-2 focus:ring-staxmagenta placeholder:text-ink/35"
                />
                <button
                  data-testid="cart-custom-add"
                  onClick={addCustom}
                  className="inline-flex items-center justify-center gap-2 border-2 border-ink bg-ink px-5 py-3 font-display uppercase text-sm text-white transition-transform hover:-translate-y-0.5"
                >
                  <Plus size={16} /> Add to Cart
                </button>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <Reveal delay={0.1}>
              <div data-testid="cart-summary" className="border-4 border-ink bg-ink text-white shadow-hard-yellow">
                <div className="flex items-center justify-between border-b-4 border-white/15 px-6 py-4">
                  <span className="flex items-center gap-2 font-display uppercase text-xl">
                    <ShoppingCart size={20} className="text-staxyellow" /> Your Cart
                  </span>
                  <span data-testid="cart-count" className="flex h-8 w-8 items-center justify-center rounded-full bg-staxmagenta font-display text-sm">
                    {totalItems + (customRequest ? 1 : 0)}
                  </span>
                </div>
                <div className="p-6">
                  {cartEmpty && (
                    <p data-testid="cart-empty" className="font-mono2 text-xs uppercase tracking-[0.2em] text-white/50 leading-relaxed">
                      Cart's empty.<br />Pick something bold.
                    </p>
                  )}
                  {lines.length > 0 && (
                    <ul className="space-y-3">
                      <AnimatePresence>
                        {lines.map((l) => (
                          <motion.li
                            key={l.key}
                            data-testid={`cart-line-${l.key}`}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="flex items-center justify-between gap-3 border-b border-white/15 pb-3"
                          >
                            <span className="flex items-center gap-2.5 text-sm font-semibold">
                              <span className="ink-dot h-3.5 w-3.5 shrink-0" style={{ background: cv(l.item.color) }} />
                              <span>
                                {l.item.name}
                                {l.opt && <span className="block font-mono2 text-[10px] uppercase tracking-widest text-white/50">{l.opt.label} · x{l.qty}</span>}
                              </span>
                            </span>
                            <span className="font-mono2 text-xs text-staxyellow">
                              {l.price ? `$${l.price * l.qty}` : `x${l.qty}`}
                            </span>
                          </motion.li>
                        ))}
                      </AnimatePresence>
                    </ul>
                  )}
                  {customRequest && (
                    <div data-testid="cart-custom-line" className="mt-4 flex items-start justify-between gap-3 border-2 border-dashed border-staxmagenta/60 px-3 py-2.5">
                      <span className="text-sm font-semibold text-white">
                        <span className="mb-0.5 block font-mono2 text-[10px] uppercase tracking-widest text-staxmagenta">Custom request</span>
                        "{customRequest}"
                      </span>
                      <button
                        data-testid="cart-custom-remove"
                        onClick={() => setCustomRequest("")}
                        aria-label="Remove custom request"
                        className="mt-0.5 shrink-0 text-white/40 hover:text-staxmagenta"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                  {estimate > 0 && (
                    <div data-testid="cart-estimate" className="mt-5 border-2 border-staxyellow px-4 py-3">
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/50">Estimated from</p>
                      <p className="font-display text-4xl text-staxyellow">${estimate.toLocaleString()}</p>
                    </div>
                  )}
                  {customCount > 0 && (
                    <p data-testid="cart-custom-note" className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.2em] text-staxmagenta">
                      + {customCount} custom-quoted item{customCount > 1 ? "s" : ""}
                    </p>
                  )}
                  <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/40">
                    Final quote after a quick chat — every project is custom.
                  </p>
                  <button
                    data-testid="cart-checkout-button"
                    onClick={checkout}
                    disabled={cartEmpty}
                    className="group mt-5 flex w-full items-center justify-center gap-2 border-2 border-staxyellow bg-staxyellow px-5 py-4 font-display uppercase text-base text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
                  >
                    Start This Project <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </button>
                  {!cartEmpty && (
                    <button
                      data-testid="cart-clear-button"
                      onClick={() => { setCart({}); setCustomRequest(""); }}
                      className="mt-3 flex w-full items-center justify-center gap-1.5 font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 hover:text-staxmagenta"
                    >
                      <Trash2 size={12} /> Clear cart
                    </button>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
